import { gql, useSuspenseQuery } from '@apollo/client';
import React, { useEffect } from 'react';

import { ErrorBoundary } from 'src/components/common/errorBoundary/ErrorBoundary';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ChecksWidget } from './widgets/ChecksWidget/ChecksWidget';
import { MembersWidget, MembersWidgetFragment } from './widgets/MembersWidget';
import { NumGraphsWidget } from './widgets/NumGraphsWidget/NumGraphsWidget';
import { PlanInfoWidget } from './widgets/PlanInfoWidget';
import { RequestsWidget } from './widgets/RequestsWidget/RequestsWidget';
import { SchemaPublishesWidget } from './widgets/SchemaPublishesWidget';
import { SupportTicketsWidget } from './widgets/SupportTicketsWidget';

const CHAMPION_DASHBOARD_QUERY = gql<
  GraphQLTypes.ChampionDashboardQuery,
  GraphQLTypes.ChampionDashboardQueryVariables
>`
  query ChampionDashboardQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      ...MembersWidgetFragment
    }
  }
  ${MembersWidgetFragment}
`;

const CHAMPION_DASHBOARD_POLLING_QUERY = gql<
  GraphQLTypes.ChampionDashboardPollingQuery,
  GraphQLTypes.ChampionDashboardPollingQueryVariables
>`
  query ChampionDashboardPollingQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      ...MembersWidgetFragment
    }
  }
  ${MembersWidgetFragment}
`;

export const ChampionDashboardPage = ({
  accountId,
  maxRangeInDays,
}: {
  accountId: string;
  maxRangeInDays: number;
}) => {
  const { client } = useSuspenseQuery(CHAMPION_DASHBOARD_QUERY, {
    variables: {
      accountId,
    },
    skip: !accountId,
  });

  useEffect(() => {
    const subscription = client
      .watchQuery({
        query: CHAMPION_DASHBOARD_POLLING_QUERY,
        pollInterval: 60_000,
        variables: {
          accountId,
        },
        skipPollAttempt: () => !accountId,
      })
      .subscribe(() => {
        // no-op since we aren't interested in the results here, just using this to handle polling the subset of data
      });
    return () => subscription?.unsubscribe();
  }, [accountId, client]);

  return (
    <ErrorBoundary>
      <div className="grid grid-cols-6 gap-4 p-4">
        <PlanInfoWidget accountId={accountId} />
        <MembersWidget accountId={accountId} />
        <SupportTicketsWidget accountId={accountId} />
        <NumGraphsWidget accountId={accountId} />
        <RequestsWidget accountId={accountId} maxRangeInDays={maxRangeInDays} />
        <SchemaPublishesWidget accountId={accountId} />
        <ChecksWidget accountId={accountId} />
      </div>
    </ErrorBoundary>
  );
};
